import { Box, Grid, Theme } from '@mui/material';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import React, { FC, useCallback, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';
import product3IllustrationImg from '../../../../assets/products/product-3/product3Illustration.png';
import { localized, localizedInterpolation } from '../../../../i18n/i18n';
import { AppState } from '../../../../state/store';
import {
  getUnitLenghtByCulture,
  getValueLengthWithUnitByCulture
} from '../../../../utilities/helpers/culture-settings-helper';
import { manuelNumberCheck } from '../../../../utilities/helpers/form-helper';
import { getConfigurationParamByNameAndCulture } from '../../../../utilities/helpers/product-helper';
import { ImageRow } from '../../../components/custom-components/configuration/dynamic-components/image-row';
import { ValueRangeInput } from '../../../components/custom-components/configuration/dynamic-components/value-range-input';
import { tableTravel, worktopDepth, worktopLength } from '../configuration-constants';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    grid: {
      width: '686px',
      [theme.breakpoints.down('lg')]: {
        width: '500px',
      },
    },
    gridItem: {
      height: '140px',
    },
  }),
);

export interface PropsFromParent {}

export const Product3Step1: FC<PropsFromParent> = React.memo(({...props}) => {
  const classes = useStyles();
  const selectedProduct = useSelector((store: AppState) => store.projectReducer.selectedProduct);
  const {unitsId} = useSelector((store: AppState) => store.urlSettingsReducer.cultureUrlParameters);
  const formContext = useFormContext();

  const getTableTravelDefaultValue = useMemo(() => {
    const defaultValue = getConfigurationParamByNameAndCulture(selectedProduct, tableTravel, unitsId);
    if (defaultValue) {
      return defaultValue;
    } else {
      return getUnitLenghtByCulture(unitsId, '300');
    }
  }, [selectedProduct, unitsId]);

  const between1400And3000 = useCallback(
    (event: any) => {
      manuelNumberCheck(
        event.target.name,
        event.target.value,
        getUnitLenghtByCulture(unitsId, '1400'),
        getUnitLenghtByCulture(unitsId, '3000'),
        unitsId,
        formContext,
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [unitsId],
  );

  const between700And1000 = useCallback(
    (event: any) => {
      manuelNumberCheck(
        event.target.name,
        event.target.value,
        getUnitLenghtByCulture(unitsId, '700'),
        getUnitLenghtByCulture(unitsId, '1000'),
        unitsId,
        formContext,
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [unitsId],
  );

  const between200And300 = useCallback(
    (event: any) => {
      manuelNumberCheck(
        event.target.name,
        event.target.value,
        getUnitLenghtByCulture(unitsId, '200'),
        getUnitLenghtByCulture(unitsId, '300'),
        unitsId,
        formContext,
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [unitsId],
  );

  return (
    <>
      <Box display="flex" justifyContent="center" alignItems="center" sx={{flexGrow: 1}}>
        <Grid container spacing={2} className={classes.grid}>
          <Grid container spacing={6}>
            <ImageRow imgString={product3IllustrationImg} />
          </Grid>

          <Grid container spacing={6} className={classes.gridItem}>
            <ValueRangeInput
              formName={worktopLength}
              minValue={'1400'}
              maxValue={'3000'}
              textInputLabel={localized('LengthOfTheTableTop') + ' (A)'}
              tooltipLabel={localizedInterpolation('ToolTipProduct3Step1_1', {
                value1: getValueLengthWithUnitByCulture(unitsId, '1400'),
                value2: getValueLengthWithUnitByCulture(unitsId, '3000'),
              })}
              formOnChange={between1400And3000}
            />
          </Grid>

          <Grid container spacing={6} className={classes.gridItem}>
            <ValueRangeInput
              formName={worktopDepth}
              minValue={'700'}
              maxValue={'1000'}
              textInputLabel={localized('DepthOfTheTableTop') + ' (B)'}
              tooltipLabel={localizedInterpolation('ToolTipProduct3Step1_2', {
                value1: getValueLengthWithUnitByCulture(unitsId, '700'),
                value2: getValueLengthWithUnitByCulture(unitsId, '1000'),
              })}
              formOnChange={between700And1000}
            />
          </Grid>

          <Grid container spacing={6} className={classes.gridItem}>
          <ValueRangeInput
              formName={tableTravel}
              minValue={'200'}
              maxValue={'300'}
              defaultValue={getTableTravelDefaultValue}
              textInputLabel={localized('WhatHeightAdjustmentIsDesired')}
              tooltipLabel={localizedInterpolation('ToolTipProduct2Step1_4', {
                value1: getValueLengthWithUnitByCulture(unitsId, '200'),
                value2: getValueLengthWithUnitByCulture(unitsId, '300'),
              })}
              formOnChange={between200And300}
            />
          </Grid>
        </Grid>
      </Box>
    </>
  );
});
